import React, { useMemo, useState } from 'react';
import { bulkInitiateCalls } from '../services/api';
import { buildDialoutPhoneParts, fullDialoutNumber } from '../utils/phoneIndia';

const PHONE_REGEX = /^\+?[1-9]\d{6,14}$/;

function parseCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return [];
  }

  const headers = lines[0].split(',').map((item) => item.trim().toLowerCase());

  return lines.slice(1).map((line, index) => {
    const values = line.split(',').map((item) => item.trim());
    const row = { rowNumber: index + 2 };
    headers.forEach((header, i) => {
      row[header] = values[i] || '';
    });
    return row;
  });
}

function normalizeRow(row) {
  const employeeName = row.employee_name || row.name || '';
  const rawPhone = row.phone || row.mobile || '';
  const parts = buildDialoutPhoneParts(rawPhone);
  const fullPhone = fullDialoutNumber(parts);
  const validationLast4 = String(row.validation_last4_expected || row.last4 || '').replace(/\D/g, '').slice(-4);

  let error = '';
  if (!employeeName.trim()) {
    error = 'Missing name';
  } else if (!parts.phone) {
    error = 'Missing phone';
  } else if (!PHONE_REGEX.test(fullPhone)) {
    error = 'Invalid phone number';
  }

  return {
    rowNumber: row.rowNumber,
    employee_name: employeeName.trim(),
    phone: parts.phone,
    country_code: parts.country_code,
    dial_as: fullPhone,
    validation_last4_expected: validationLast4,
    valid: !error,
    error,
  };
}

function BulkUpload() {
  const [voice, setVoice] = useState('female');
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const normalizedRows = useMemo(() => rows.map((row) => normalizeRow(row)), [rows]);

  const validRows = normalizedRows.filter((row) => row.valid);
  const invalidRows = normalizedRows.filter((row) => !row.valid);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError('');
    setResult(null);

    if (!file) {
      setRows([]);
      setFileName('');
      return;
    }

    try {
      const text = await file.text();
      const parsedRows = parseCsv(text);
      if (parsedRows.length === 0) {
        setError('CSV is empty or missing data rows.');
        setRows([]);
        setFileName(file.name);
        return;
      }
      setRows(parsedRows);
      setFileName(file.name);
    } catch (err) {
      setError('Failed to read CSV file.');
      setRows([]);
    }
  };

  const handleSubmit = async () => {
    if (validRows.length === 0) {
      setError('Upload a CSV with at least one valid row.');
      return;
    }

    setSubmitting(true);
    setError('');
    setResult(null);

    try {
      const response = await bulkInitiateCalls({
        voice,
        rows: validRows,
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit bulk calls.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Bulk Upload</h4>
          <p className="text-muted mb-0">Upload a CSV of customer names and phone numbers for mass calling (India +91)</p>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold">IVR voice</label>
              <select className="form-select" value={voice} onChange={(e) => setVoice(e.target.value)} disabled={submitting}>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-semibold">CSV File</label>
              <input
                type="file"
                className="form-control"
                accept=".csv"
                onChange={handleFileChange}
                disabled={submitting}
              />
              <div className="form-text">
                Headers: <code>name</code> or <code>employee_name</code>, <code>phone</code> (10 digits or with 91), optional{' '}
                <code>last4</code>. Numbers use India (+91); leading 91 is not duplicated.
              </div>
            </div>
          </div>

          {fileName && (
            <div className="mt-3 small text-muted">
              Loaded file: <strong>{fileName}</strong>
            </div>
          )}

          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body">
              <h6 className="fw-bold">Summary</h6>
              <p className="mb-2">Total rows: <strong>{normalizedRows.length}</strong></p>
              <p className="mb-2 text-success">Valid rows: <strong>{validRows.length}</strong></p>
              <p className="mb-0 text-danger">Invalid rows: <strong>{invalidRows.length}</strong></p>
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body d-flex flex-column justify-content-between">
              <div>
                <h6 className="fw-bold">Ready to Call</h6>
                <p className="text-muted mb-0">
                  Only valid rows will be submitted for dialing. Invalid rows stay in the preview for correction.
                </p>
              </div>
              <div className="mt-3">
                <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || validRows.length === 0}>
                  {submitting ? 'Submitting...' : `Start Mass Calling (${validRows.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Row</th>
                  <th>Name</th>
                  <th>Dial as</th>
                  <th>Last 4</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {normalizedRows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">
                      Upload a CSV to preview rows.
                    </td>
                  </tr>
                ) : (
                  normalizedRows.map((row) => (
                    <tr key={row.rowNumber}>
                      <td className="ps-4">{row.rowNumber}</td>
                      <td>{row.employee_name || '—'}</td>
                      <td>
                        <code className="small">{row.dial_as || '—'}</code>
                      </td>
                      <td>{row.validation_last4_expected || '—'}</td>
                      <td>
                        {row.valid ? (
                          <span className="badge bg-success">Valid</span>
                        ) : (
                          <span className="badge bg-danger">{row.error}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {result && (
        <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 16 }}>
          <div className="card-body">
            <h6 className="fw-bold">Submission Result</h6>
            <p className="mb-2 text-success">Submitted: <strong>{result.submitted}</strong></p>
            <p className="mb-3 text-danger">Rejected: <strong>{result.rejected}</strong></p>
            {result.rejectedRows?.length > 0 && (
              <div className="small text-muted">
                {result.rejectedRows.map((row) => (
                  <div key={`rejected-${row.rowNumber}`}>
                    Row {row.rowNumber}: {row.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkUpload;
